import { MAP_DEFAULTS, MAP_STYLE_CATALOGUE, mapStyleKey } from '@pz/scene';

/**
 * The map looks a shopper can choose between.
 *
 * Customily's twelve styles are Mapbox styles owned by `martumo` — its supplier's
 * account, private to it. Our token gets "Style not found" for every one, and no
 * amount of licensing on our side changes that: they are someone else's property.
 *
 * Each entry names a Mapbox style to fetch tiles from, plus an optional colour
 * matrix. There are two grades of entry here and it matters which is which:
 *
 *  - `source: 'personalizy/…'` — one of OUR styles, published by
 *    `pnpm --filter @pz/migrate exec tsx src/cli.ts map-styles`. Exact: the land,
 *    water and road colours are set directly and there are no labels, matching
 *    Customily's originals, which draw none.
 *  - `source: 'mapbox/…'` with a matrix — an APPROXIMATION over a stock style,
 *    used until ours are published. Measured against Customily's own reference
 *    renders, these cannot be made exact: eight of the twelve want roads darker
 *    than the land where every stock base draws them lighter, eight need three or
 *    four independent inks where a matrix has one ramp, and none can drop the
 *    street labels that raster tiles bake in. See `apps/migrate/src/mapbox-styles.ts`.
 *
 * Names match Customily's exactly, so a migrated swatch keeps working: the label
 * the shopper picked is the key looked up here.
 */

export interface MapStyle {
  /** The label the shopper sees, as Customily spelled it. */
  label: string;
  /** Mapbox style the tiles come from, `owner/style-id`. */
  source: string;
  /**
   * Colour matrix applied to the tiles, in Skia's 4×5 row-major form.
   * Omitted where the base style is already the look.
   */
  matrix?: number[];
}

/** Rec. 709 luminance weights — the same ones the greyscale filter uses. */
const LR = 0.2126;
const LG = 0.7152;
const LB = 0.0722;

/**
 * Maps luminance onto a ramp between two colours: black becomes `dark`, white
 * becomes `light`, everything else lands in between.
 *
 * A duotone rather than a flat tint, because a flat tint turns a map's white
 * background into solid colour and loses every road. Channels are 0–1.
 */
function duotone(dark: [number, number, number], light: [number, number, number]): number[] {
  const row = (i: number): number[] => {
    const span = light[i]! - dark[i]!;
    return [LR * span, LG * span, LB * span, 0, dark[i]!];
  };
  return [...row(0), ...row(1), ...row(2), 0, 0, 0, 1, 0];
}

/** Pushes colours away from grey (>1) or towards it (<1), leaving luminance alone. */
function saturate(k: number): number[] {
  const r = (1 - k) * LR;
  const g = (1 - k) * LG;
  const b = (1 - k) * LB;
  return [
    r + k, g, b, 0, 0,
    r, g + k, b, 0, 0,
    r, g, b + k, 0, 0,
    0, 0, 0, 1, 0,
  ];
}

const STREETS = 'mapbox/streets-v12';
const DARK = 'mapbox/dark-v11';
/** Customily's "Satellite" is aerial imagery with the street network on top. */
const SATELLITE = 'mapbox/satellite-streets-v12';
/** Customily's "Light" is Mapbox's own pale base, not one of our repaints. */
const LIGHT = 'mapbox/light-v11';
const MONOCHROME_WHITE = 'personalizy/cmt49sh6o006u01sc36ey0ik7';
const MODERN = 'personalizy/cmt49sin4007701shdn9bc4pm';

/** Keyed by a slug of the label; see `mapStyleKey`. */
/**
 * Keyed by a slug of the label; see `mapStyleKey`.
 *
 * Every custom look is one of OUR styles now — land, water and road colours set
 * directly from Customily's own reference renders, and no label layers at all,
 * because none of the originals draw street names. Streets and Dark stay on
 * Mapbox's stock styles because that is exactly what Customily used for those two.
 */
export const MAP_STYLES: Record<string, MapStyle> = {
  streets: { label: 'Streets', source: STREETS },
  satellite: { label: 'Satellite', source: SATELLITE },
  light: { label: 'Light', source: LIGHT },
  dark: { label: 'Dark', source: DARK },

  'noir': { label: 'Noir', source: 'personalizy/cmt49sgz9002q01sh9p5g4ugx' },
  'monochrome-white': { label: 'Monochrome - White', source: MONOCHROME_WHITE },
  'vintage': { label: 'Vintage', source: 'personalizy/cmt49shgp008d01s30cuw5u2z' },
  'romantic': { label: 'Romantic', source: 'personalizy/cmt49shof002r01sh29aohhu9' },
  'pop-art': { label: 'Pop Art', source: 'personalizy/cmt49shwl008201qze54oaru5' },
  'sand': { label: 'Sand', source: 'personalizy/cmt49si3p007b01sgg5or49cs' },
  'blueprint': { label: 'Blueprint', source: 'personalizy/cmt49siet002s01shdu897zfw' },
  'modern': { label: 'Modern', source: MODERN },
  'monochrome-dark-blue': { label: 'Monochrome - Dark Blue', source: 'personalizy/cmt49siwb002t01sh3o2gffjp' },
  'monochrome-light-blue': { label: 'Monochrome - Light Blue', source: 'personalizy/cmt49sj5i008301qz3rtxfol0' },
  'monochrome-blue': { label: 'Monochrome - Blue', source: 'personalizy/cmt49sjgy007c01sg13iaeauf' },
  'monochrome-green': { label: 'Monochrome - Green', source: 'personalizy/cmt49sjpt008e01s32cdr25d5' },
  'monochrome-red': { label: 'Monochrome - Red', source: 'personalizy/cmtqbzj9h00gh01r29pgkf4yo' },
  'monochrome-black': { label: 'Monochrome - Black', source: 'personalizy/cmtqbzjim00fq01pmfje0bp8e' },

  // Two names our own scenes used before any of this, kept so templates authored
  // against them keep rendering. `light` used to be a third such alias — it
  // pointed at Modern, a red-and-cream repaint, which is nobody's idea of a
  // light map. Customily has a real "Light" (Mapbox's pale base) and now so do
  // we, so the alias is gone and the key means what it says. Templates that
  // chose `light` expecting Modern draw paler; that is the correction, not a
  // regression — the alias was the bug.
  color: { label: 'Streets', source: STREETS },
  gray: { label: 'Monochrome - White', source: MONOCHROME_WHITE },
};

export const DEFAULT_MAP_STYLE = mapStyleKey(MAP_DEFAULTS.style);

export { mapStyleKey };

/**
 * The style for a name, falling back to the default rather than failing.
 *
 * A map that draws in the wrong palette is a complaint; a map that does not draw
 * is a blank rectangle on a printed poster.
 */
export function resolveMapStyle(name: string | undefined): MapStyle {
  if (!name) return MAP_STYLES[DEFAULT_MAP_STYLE]!;
  // A style of the shop's own, pasted from Mapbox Studio as
  // `mapbox://styles/<user>/<id>`: drawn through the default's settings with
  // that style's tiles — Customily's "custom Mapbox style URL".
  const custom = /^mapbox:\/\/styles\/([\w-]+\/[\w-]+)\/?$/i.exec(name.trim());
  if (custom) {
    return { ...MAP_STYLES[DEFAULT_MAP_STYLE]!, label: 'Custom', source: custom[1]! };
  }
  return (
    MAP_STYLES[mapStyleKey(name)] ?? MAP_STYLES[name] ?? MAP_STYLES[DEFAULT_MAP_STYLE]!
  );
}

/**
 * Every style, for a picker, in Customily's own grid order.
 *
 * The order is data, not a sort: Customily groups the two stock bases first,
 * then the photographic and plain ones, then the monochromes light-to-dark, and
 * a merchant comparing the two apps side by side is comparing grids. It lives in
 * `@pz/scene` because the server needs the same list without CanvasKit.
 *
 * Aliases (`color`, `gray`) are not listed: they are old spellings of styles
 * already in the grid, not looks of their own.
 */
export function listMapStyles(): Array<{ key: string; label: string }> {
  return MAP_STYLE_CATALOGUE.filter((entry) => MAP_STYLES[entry.key]).map((entry) => ({
    key: entry.key,
    label: entry.label,
  }));
}

/**
 * The tile URL for one style, or undefined when there is no Mapbox token.
 *
 * There is deliberately NO OpenStreetMap fallback. OSM's volunteer servers have
 * already blocked this project under their tile usage policy, and — this is the
 * dangerous part — they serve the refusal as a 200 with a perfectly drawable PNG
 * that reads "Access blocked". A fallback would not degrade the map, it would
 * print that notice onto a customer's poster. Without a token the map reports
 * itself unrenderable instead, which is loud and catches the problem in QA.
 *
 * 256px tiles, because that is the grid `prepareMap`'s Web Mercator arithmetic
 * lays out — asking Mapbox for 512s indexes the same extents but at a different
 * pixel scale, and mixing the two silently prints the map at the wrong size.
 * `@2x` keeps the resolution: the image arrives 512px and is drawn into the same
 * 256-unit slot, so a map stays sharp at 300 DPI without changing the geometry.
 */
export function mapTileUrl(
  z: number,
  x: number,
  y: number,
  source: string,
  token: string | undefined,
): string | undefined {
  if (!token) return undefined;
  return (
    `https://api.mapbox.com/styles/v1/${source}/tiles/256/${z}/${x}/${y}@2x` +
    `?access_token=${encodeURIComponent(token)}`
  );
}
